import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { JobType } from "@/hooks/use-job-types";
import { useCreateJobType, useUpdateJobType } from "@/hooks/use-job-types";
import type { CreateJobTypeInput, UpdateJobTypeInput } from "@/schemas/categories";
import { CreateJobTypeSchema, UpdateJobTypeSchema } from "@/schemas/categories";

// ── New Job Type ───────────────────────────────────────────────────────────────

interface NewJobTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewJobTypeDialog({ open, onOpenChange }: NewJobTypeDialogProps) {
  const createJobType = useCreateJobType();
  const form = useForm<CreateJobTypeInput>({
    resolver: zodResolver(CreateJobTypeSchema),
    defaultValues: { code: "", label: "", active: true },
  });

  function onSubmit(values: CreateJobTypeInput) {
    createJobType.mutate(values, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Job Type</DialogTitle>
          <DialogDescription>
            Create a new job type code. The code cannot be changed after creation.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. NMP"
                      className="font-mono uppercase"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Noise Mitigation Plan" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createJobType.isPending}>
                {createJobType.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Job Type ──────────────────────────────────────────────────────────────

interface EditJobTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobType: JobType;
}

export function EditJobTypeDialog({ open, onOpenChange, jobType }: EditJobTypeDialogProps) {
  const updateJobType = useUpdateJobType();
  const form = useForm<UpdateJobTypeInput>({
    resolver: zodResolver(UpdateJobTypeSchema),
    defaultValues: { label: jobType.label, active: jobType.active },
  });

  useEffect(() => {
    if (open) {
      form.reset({ label: jobType.label, active: jobType.active });
    }
  }, [open, jobType, form]);

  function onSubmit(values: UpdateJobTypeInput) {
    updateJobType.mutate({ id: jobType.id, ...values }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Job Type</DialogTitle>
          <DialogDescription>
            Code <strong className="font-mono">{jobType.code}</strong> is fixed and cannot be
            changed.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Code</p>
              <p className="font-mono text-sm text-muted-foreground bg-muted rounded px-3 py-2">
                {jobType.code}
              </p>
            </div>
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateJobType.isPending}>
                {updateJobType.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
